import { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { api, apiError } from "../lib/apiClient";
import { toast } from "sonner";
import { Brain, FileText, Code2, Loader2, CheckCircle2, XCircle } from "lucide-react";

const TABS = [
  { id: "quiz", label: "QUIZ", icon: Brain },
  { id: "document", label: "DOCUMENT", icon: FileText },
  { id: "code", label: "CODE", icon: Code2 },
];

export function SkillsDrawer({ open, onClose }) {
  const [tab, setTab] = useState("quiz");

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent
        data-testid="skills-drawer"
        side="left"
        className="w-full sm:max-w-lg bg-[var(--panel-solid)] border-r border-[var(--border-hud)] text-white overflow-y-auto"
      >
        <SheetHeader>
          <SheetTitle className="font-display tracking-[0.15em] accent-text text-glow">MODULES</SheetTitle>
          <SheetDescription className="font-mono-hud text-[10px] tracking-[0.2em] text-[#8b949e]">
            Quiz, analyse de document et assistant de code, propulsés par Sara.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-5 flex border border-[var(--border-hud)] clip-corner overflow-hidden">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                data-testid={`skill-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`flex-1 py-2.5 flex items-center justify-center gap-2 font-mono-hud text-[10px] tracking-[0.15em] uppercase transition-colors ${
                  tab === t.id ? "bg-[var(--accent-a25)] text-white" : "text-[#8b949e] hover:text-white"
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                {t.label}
              </button>
            );
          })}
        </div>

        <div className="mt-6">
          {tab === "quiz" && <QuizTool />}
          {tab === "document" && <DocumentTool />}
          {tab === "code" && <CodeTool />}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function RunButton({ loading, children, testid, disabled }) {
  return (
    <button
      data-testid={testid}
      type="submit"
      disabled={loading || disabled}
      className="hud-btn clip-corner w-full py-3 flex items-center justify-center gap-2 text-sm font-bold disabled:opacity-50"
    >
      {loading && <Loader2 className="h-4 w-4 animate-spin" />}
      {children}
    </button>
  );
}

const inputCls =
  "w-full bg-[rgba(2,8,18,0.8)] border border-[var(--border-hud)] px-3 py-2.5 text-sm text-white outline-none focus:border-[var(--accent)] focus:shadow-[var(--accent-glow)] transition-colors clip-corner placeholder:text-[#3a4a5a]";
const labelCls = "font-mono-hud text-[10px] tracking-[0.25em] text-[#8b949e] block mb-1.5";

/* ----------------------------- QUIZ ----------------------------- */
function QuizTool() {
  const [topic, setTopic] = useState("");
  const [count, setCount] = useState(5);
  const [loading, setLoading] = useState(false);
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const generate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setQuiz(null);
    setSubmitted(false);
    setAnswers({});
    try {
      const { data } = await api.post("/tools/quiz", { topic, count });
      setQuiz(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const score = quiz
    ? quiz.questions.reduce((acc, q, i) => acc + (answers[i] === q.answer_index ? 1 : 0), 0)
    : 0;

  return (
    <div className="space-y-4">
      <form onSubmit={generate} className="space-y-3">
        <div>
          <label className={labelCls}>SUJET DU QUIZ</label>
          <input
            data-testid="quiz-topic-input"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            required
            placeholder="ex. Le système solaire, les hooks React, la 2nde Guerre mondiale..."
            className={inputCls}
          />
        </div>
        <div>
          <label className={labelCls}>QUESTIONS · {count}</label>
          <input
            data-testid="quiz-count-slider"
            type="range"
            min="3"
            max="10"
            value={count}
            onChange={(e) => setCount(Number(e.target.value))}
            className="w-full accent-[var(--accent)]"
          />
        </div>
        <RunButton loading={loading} testid="quiz-generate-button" disabled={!topic.trim()}>
          {loading ? "GÉNÉRATION..." : "GÉNÉRER LE QUIZ"}
        </RunButton>
      </form>

      {quiz && (
        <div className="space-y-4 animate-rise">
          {quiz.title && (
            <h3 className="font-display text-lg tracking-wide accent-text text-glow">{quiz.title}</h3>
          )}
          {quiz.questions.map((q, qi) => (
            <div key={qi} className="hud-panel clip-corner p-3">
              <p className="text-sm text-[#eaf4ff] mb-2">
                <span className="accent-text font-mono-hud mr-1">{qi + 1}.</span>
                {q.question}
              </p>
              <div className="space-y-1.5">
                {q.options.map((opt, oi) => {
                  const chosen = answers[qi] === oi;
                  const correct = submitted && oi === q.answer_index;
                  const wrong = submitted && chosen && oi !== q.answer_index;
                  return (
                    <button
                      key={oi}
                      data-testid="quiz-option"
                      disabled={submitted}
                      onClick={() => setAnswers((a) => ({ ...a, [qi]: oi }))}
                      className={`w-full text-left px-3 py-2 text-[13px] clip-corner border transition-colors flex items-center gap-2 ${
                        correct
                          ? "border-[#00ffaa] bg-[rgba(0,255,170,0.12)] text-[#c9ffe9]"
                          : wrong
                          ? "border-[#ff6b6b] bg-[rgba(255,107,107,0.12)] text-[#ffd5d5]"
                          : chosen
                          ? "border-[var(--accent)] bg-[var(--accent-a15)] text-white"
                          : "border-[var(--border-hud)] text-[#c8d6e5] hover:bg-[rgba(255,255,255,0.04)]"
                      }`}
                    >
                      {correct && <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />}
                      {wrong && <XCircle className="h-3.5 w-3.5 shrink-0" />}
                      {opt}
                    </button>
                  );
                })}
              </div>
              {submitted && q.explanation && (
                <p className="mt-2 font-mono-hud text-[11px] text-[#8fa3b8] leading-snug">» {q.explanation}</p>
              )}
            </div>
          ))}

          {!submitted ? (
            <button
              data-testid="quiz-submit-button"
              onClick={() => setSubmitted(true)}
              disabled={Object.keys(answers).length < quiz.questions.length}
              className="hud-btn clip-corner w-full py-3 text-sm font-bold disabled:opacity-50"
            >
              SOUMETTRE LES RÉPONSES
            </button>
          ) : (
            <div
              data-testid="quiz-score"
              className="text-center font-display text-xl tracking-wide accent-text text-glow"
            >
              SCORE · {score} / {quiz.questions.length}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* --------------------------- DOCUMENT --------------------------- */
function DocumentTool() {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/tools/document", { text });
      setResult(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <form onSubmit={run} className="space-y-3">
        <div>
          <label className={labelCls}>COLLEZ UN DOCUMENT / TEXTE</label>
          <textarea
            data-testid="document-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            required
            rows={7}
            placeholder="Collez un contrat, un article, une politique ou des notes..."
            className={inputCls + " resize-none"}
          />
        </div>
        <RunButton loading={loading} testid="document-analyze-button" disabled={!text.trim()}>
          {loading ? "ANALYSE..." : "ANALYSER"}
        </RunButton>
      </form>

      {result && (
        <div data-testid="skill-result" className="space-y-4 animate-rise">
          {result.title && <h3 className="font-display text-base tracking-wide accent-text">{result.title}</h3>}
          {result.summary && <p className="text-sm leading-relaxed text-[#eaf4ff]">{result.summary}</p>}
          <ResultList title="POINTS CLÉS" items={result.key_points} color="var(--accent)" />
          <ResultList title="POINTS DE VIGILANCE" items={result.concerns} color="#ffab00" />
        </div>
      )}
    </div>
  );
}

function ResultList({ title, items, color }) {
  if (!items || !items.length) return null;
  return (
    <div>
      <span className="font-mono-hud text-[10px] tracking-[0.25em] uppercase" style={{ color }}>
        {title}
      </span>
      <ul className="mt-2 space-y-1.5">
        {items.map((it, i) => (
          <li key={i} className="flex gap-2 text-[13px] leading-snug text-[#dbe7f3]">
            <span className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0" style={{ background: color }} />
            {it}
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ----------------------------- CODE ----------------------------- */
function CodeTool() {
  const [mode, setMode] = useState("review");
  const [language, setLanguage] = useState("");
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState("");

  const run = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResult("");
    try {
      const { data } = await api.post("/tools/code", { mode, language, content });
      setResult(data.result);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <form onSubmit={run} className="space-y-3">
        <div className="flex border border-[var(--border-hud)] clip-corner overflow-hidden">
          {[
            { id: "review", label: "réviser" },
            { id: "generate", label: "générer" },
          ].map((m) => (
            <button
              key={m.id}
              type="button"
              data-testid={`code-mode-${m.id}`}
              onClick={() => setMode(m.id)}
              className={`flex-1 py-2 font-mono-hud text-[10px] tracking-[0.15em] uppercase transition-colors ${
                mode === m.id ? "bg-[var(--accent-a25)] text-white" : "text-[#8b949e] hover:text-white"
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>
        <div>
          <label className={labelCls}>LANGAGE (OPTIONNEL)</label>
          <input
            data-testid="code-language-input"
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            placeholder="python, javascript, rust..."
            className={inputCls}
          />
        </div>
        <div>
          <label className={labelCls}>{mode === "generate" ? "DÉCRIVEZ CE QU'IL FAUT CRÉER" : "COLLEZ LE CODE"}</label>
          <textarea
            data-testid="code-input"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            required
            rows={7}
            placeholder={mode === "generate" ? "Une fonction qui debounce des appels asynchrones..." : "Collez le code à réviser..."}
            className={inputCls + " resize-none font-mono-hud text-[13px]"}
          />
        </div>
        <RunButton loading={loading} testid="code-run-button" disabled={!content.trim()}>
          {loading ? "TRAITEMENT..." : mode === "generate" ? "GÉNÉRER LE CODE" : "RÉVISER LE CODE"}
        </RunButton>
      </form>

      {result && (
        <pre
          data-testid="code-result"
          className="animate-rise hud-panel clip-corner p-3 text-[12px] leading-relaxed text-[#dbe7f3] whitespace-pre-wrap font-mono-hud overflow-x-auto"
        >
          {result}
        </pre>
      )}
    </div>
  );
}
